import { type CurrencyCode } from "@doitintl/cmp-models";
import { darken, lighten, useTheme } from "@mui/material/styles";
import * as Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { DateTime } from "luxon";

import { formatValueWithCurrency } from "../../Components/hooks/useFormatter";
import { ThemeModes } from "../../muiThemeTypes";
import { getCurrencySymbol } from "../../utils/common";
import { type DataPoint, type SeriesData } from "./types";

interface ChartSeriesOptions {
  name: string;
  data: DataPoint[];
  type: string;
  color: string;
  dashStyle?: string;
  marker?: {
    enabled: boolean;
  };
}

interface ChartProps {
  seriesData: SeriesData;
  currencyCode: CurrencyCode;
  marketplaceLimitAmount?: number;
}

export const calculateForecast = (data: DataPoint[], endDate: number): DataPoint[] => {
  if (data.length === 0) return [];
  const regressionData = [...data];
  // remove that last point if it's not a complete month
  const now = DateTime.now().toUTC().toJSDate();
  if (regressionData[regressionData.length - 1].x < now.getTime()) {
    regressionData.pop();
  }

  if (regressionData.length < 2) return [];

  const lastPoint = regressionData[regressionData.length - 1];
  if (lastPoint.x >= endDate) return [];

  // Calculate linear regression using least squares method
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;
  const n = regressionData.length;
  const forecastPoints: DataPoint[] = [];

  regressionData.forEach((point) => {
    sumX += point.x;
    sumY += point.y;
    sumXY += point.x * point.y;
    sumXX += point.x * point.x;
  });

  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  // Add the last actual point to the forecast series for continuity
  forecastPoints.push(lastPoint);

  // Calculate the forecast point at the end date
  const forecastY = slope * endDate + intercept;
  forecastPoints.push({ x: endDate, y: forecastY > 0 ? forecastY : 0 });

  return forecastPoints;
};

export const CommitmentChart = ({ seriesData, currencyCode, marketplaceLimitAmount }: ChartProps) => {
  const theme = useTheme();
  const currencySymbol = getCurrencySymbol(currencyCode);

  const chartSeries: ChartSeriesOptions[] = [
    {
      name: "Total eligible",
      data: seriesData.totalEligible,
      color: theme.palette.info.light,
      type: "area",
    },
    {
      name: "Commitment",
      data: seriesData.commitment,
      type: "line",
      color:
        theme.palette.mode === ThemeModes.LIGHT
          ? lighten(theme.palette.text.disabled, 0.2)
          : darken(theme.palette.text.disabled, 0.4),
    },
  ];

  const forecastSeries =
    seriesData.commitment.length > 0
      ? calculateForecast(seriesData.totalEligible, seriesData.commitment[seriesData.commitment.length - 1].x)
      : [];

  if (forecastSeries.length > 0) {
    chartSeries.push({
      name: "Forecast",
      data: forecastSeries,
      type: "line",
      dashStyle: "ShortDash",
      color:
        theme.palette.mode === ThemeModes.LIGHT
          ? lighten(theme.palette.text.disabled, 0.2)
          : darken(theme.palette.text.disabled, 0.4),
      marker: {
        enabled: false,
      },
    });
  }

  const hasNonZeroMarketplaceData = seriesData.marketplace?.some((point) => point.y > 0);
  if (hasNonZeroMarketplaceData) {
    chartSeries.splice(1, 0, {
      name: "Marketplace",
      data: seriesData.marketplace,
      type: "area",
      color: theme.palette.success.main,
    });
  }

  return (
    <HighchartsReact
      highcharts={Highcharts}
      allowChartUpdate={true}
      options={{
        chart: {
          height: 320,
          backgroundColor: theme?.palette?.background.paper,
        },
        exporting: {
          enabled: false,
        },
        title: {
          text: "",
        },
        xAxis: {
          left: 38,
          type: "datetime",
          labels: {
            style: {
              color: theme?.palette?.text?.primary,
            },
          },
          accessibility: {
            rangeDescription: "",
          },
        },
        yAxis: {
          title: {
            text: "",
          },
          labels: {
            style: {
              color: theme?.palette?.text?.primary,
            },
          },
          plotLines:
            marketplaceLimitAmount && marketplaceLimitAmount > 0
              ? [
                  {
                    value: marketplaceLimitAmount,
                    color: theme.palette.divider,
                    width: 3,
                    zIndex: 3,
                    label: {
                      text: `${formatValueWithCurrency(marketplaceLimitAmount, 0, currencyCode, true)} Marketplace limit`,
                      align: "left",
                      style: {
                        color: theme.palette.text.secondary,
                      },
                    },
                  },
                ]
              : [],
        },
        tooltip: {
          shared: true,
          pointFormat: `<span style="color:{point.color}">\u25CF<b> {series.name}</b></span> : {point.y}<br/>`,
          valuePrefix: currencySymbol,
          valueDecimals: 0,
        },
        credits: {
          enabled: false,
        },
        plotOptions: {
          series: {
            animation: true,
          },
          area: {
            fillOpacity: 0.35,
          },
        },
        series: chartSeries,
        legend: {
          layout: "horizontal",
          enabled: true,
          itemStyle: {
            color: theme.palette.text.secondary,
            fontWeight: 400,
          },
        },
      }}
    />
  );
};
